import type {
  AnchorHTMLAttributes,
  ComponentType,
  ImgHTMLAttributes,
  ReactNode,
} from 'react'
import { useContext, useMemo } from 'react'

import type { CleaveOptions } from 'cleave.js/options'

import type { CallingCode, format } from '@twisto/form'
import { createSharedContext } from '@twisto/utils'

import type { I18nLanguage } from './i18n-provider'

/**
 * Design-system runtime context.
 *
 * Replaces the prior `useAppConfig` coupling that pulled host-app concerns
 * (region, env-config, feature flags) into the DS. The DS only reads what
 * it strictly needs for formatting and host-side error messaging; everything
 * else stays in the host application.
 *
 * In Phase 3 this will be expanded with LinkComponent / ImageComponent /
 * featureFlag injection and exposed publicly as `DesignSystemProvider`.
 */

export type PresetKey = 'zipCode' | 'personalId' | 'date' | 'money'

export type FormatPresets = Record<PresetKey, CleaveOptions> & {
  date: Record<I18nLanguage, format.CleaveDateOptions>
  money: Record<I18nLanguage, format.CleaveMoneyOptions>
}

export type CurrencyPresets = Record<I18nLanguage, format.CurrencyOptions>

export type FormattedOptions = {
  presets: FormatPresets
  currency: CurrencyPresets
}

export type SupportInfo = {
  email: string
  phoneNumber: string
  hoursFrom: number
  hoursTo: number
  days: 'daily' | 'mo-fr'
}

/**
 * Anchor-shaped component the host app provides for internal navigation
 * (e.g. Next.js `<Link>`, react-router `<Link>`). The DS uses it whenever
 * a component receives an internal `to` path so the host's router takes
 * over instead of a hard page reload.
 *
 * Defaults to a plain `<a>` (which still works — just no client-side
 * navigation). The `replace` prop is optional and ignored by the default.
 */
export type LinkComponentProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string
  replace?: boolean
  children?: ReactNode
}

export type LinkComponent = ComponentType<LinkComponentProps>

/**
 * Image-shaped component the host app provides for assets that benefit
 * from framework-level image optimisation (e.g. Next.js `<Image>`).
 * Defaults to a plain `<img>` when absent.
 */
export type ImageComponentProps = ImgHTMLAttributes<HTMLImageElement> & {
  src: string
  alt: string
  width?: number | string
  height?: number | string
}

export type ImageComponent = ComponentType<ImageComponentProps>

export type DesignSystemContextValue = {
  /** ISO-4217 currency code used when component-level `currency` prop is absent (e.g. 'CZK', 'PLN'). */
  currencyCode?: string
  /** Calling codes (e.g. ['+420', '+421']) used by phone-number formatters. */
  callingCodes?: readonly CallingCode[]
  /** Cleave.js presets for currency / date / zipcode / personalId / money inputs. */
  formattedOptions?: FormattedOptions
  /** Support email used by the error-boundary modal (shortcut for `support.email`). */
  supportEmail?: string
  /** Full support contact info used by the help-modal organism. */
  support?: SupportInfo
  /**
   * Host-app link primitive used by DS link / navigation components for
   * internal `to` paths. Pass Next's `<Link>`, react-router's `<Link>`,
   * etc. Falls back to plain `<a href>` when absent.
   */
  LinkComponent?: LinkComponent
  /**
   * Host-app image primitive used by DS avatar / thumbnail components
   * for optimised image rendering (e.g. Next's `<Image>`). Falls back
   * to a plain `<img>` when absent.
   */
  ImageComponent?: ImageComponent
}

const DesignSystemContext = createSharedContext<DesignSystemContextValue>('base/ds-context#0', {})

type ProviderProps = {
  value: DesignSystemContextValue
  children: ReactNode
}

export const DesignSystemContextProvider = ({
  value,
  children,
}: ProviderProps) => {
  const memoised = useMemo(
    () => value,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      value.currencyCode,
      value.callingCodes,
      value.formattedOptions,
      value.supportEmail,
      value.support,
      value.LinkComponent,
      value.ImageComponent,
    ]
  )

  return (
    <DesignSystemContext.Provider value={memoised}>
      {children}
    </DesignSystemContext.Provider>
  )
}

export const useDesignSystem = () => useContext(DesignSystemContext)
